/**
 * TopStep / ProjectX Frontend Service
 * API client for the TopStep challenge trading system
 */

import api from './api';

// ─── Types ──────────────────────────────────────────────

export interface TopStepSession {
  authenticated: boolean;
  expiresIn: number;
  dryRun: boolean;
}

export interface TopStepAccount {
  id: number;
  name: string;
  balance: number;
  canTrade: boolean;
  isVisible: boolean;
  simulated?: boolean;
}

export interface TopStepAccountStatus {
  accountId: number;
  accountName: string;
  balance?: number;
  canTrade?: boolean;
  instanceId?: string;
  instanceLabel?: string;
  running: boolean;
  posture: string;
  cumPnL: number;
  killSwitchActive: boolean;
  totalUnrealized?: number;
  pnlIsRealtime?: boolean;
  positions?: TopStepPosition[];
}

export interface TopStepContract {
  id: string;
  name: string;
  description: string;
  tickSize: number;
  tickValue: number;
  symbolId?: string;
  activeContract?: boolean;
}

export interface TopStepInstrumentMeta {
  symbol: string;
  fullName: string;
  tickSize: number;
  tickValue: number;
  stopTicks: number;
  targetTicks: number;
  riskPerContract: number;
  marginPerContract: number;
  isDefault: boolean;
  isFullSize: boolean;
}

export interface TopStepInstrumentRegistryResponse {
  defaults: string[];
  instruments: TopStepInstrumentMeta[];
}

export interface TopStepPosition {
  accountId: number;
  contractId: string;
  contractName: string;
  side: number;
  size: number;
  avgEntryPrice: number;
  unrealizedPnl: number;
  realizedPnl: number;
  currentPrice?: number;
  priceTimestamp?: string;
  priceAgeMs?: number;
  pnlIsRealtime?: boolean;
  pnlUnavailableReason?: string;
}

export interface TopStepPositionsResponse {
  positions: TopStepPosition[];
  totalUnrealized: number | null;
  pnlIsRealtime?: boolean;
  realtimeCount?: number;
}

export interface TopStepOrder {
  id: number;
  accountId: number;
  contractId: string;
  type: number;
  side: number;
  size: number;
  limitPrice?: number;
  stopPrice?: number;
  status: number;
  filledSize: number;
  avgFillPrice?: number;
  createdAt: string;
  customTag?: string;
}

export interface TopStepRiskState {
  cumulativeRealizedPnl: number;
  cumulativeUnrealizedPnl: number;
  dailyRealizedPnl: number;
  sessionDate: string;
  totalTrades: number;
  wins: number;
  losses: number;
  consecutiveLosses: number;
  maxConsecutiveLosses: number;
  posture: string;
  killSwitchActive: boolean;
  killSwitchReason: string;
  dailyCapReached: boolean;
  distanceToTarget: number;
  distanceToInternalStop: number;
  distanceToHardMax: number;
  tradeLog: TradeLogEntry[];
}

export interface TradeLogEntry {
  id: string;
  symbol: string;
  side: 'LONG' | 'SHORT';
  contracts: number;
  entryPrice: number;
  exitPrice?: number;
  realizedPnl?: number;
  status: 'open' | 'closed' | 'cancelled';
  timestamp: string;   // openedAt — backend field name
  closedAt?: string;
  riskApproved?: boolean;
}

export interface ChallengeProgress {
  targetPct: number;
  riskUsedPct: number;
  postureLabel: string;
  onTrack: boolean;
  summary: string;
}

export interface AutopilotDecision {
  id: string;
  timestamp: string;
  type: string;
  symbol: string;
  action: string;
  kazeScore: number;
  tomiComposite: number;
  reason: string;
  executed: boolean;
  contracts?: number;
  pnl?: number;
  orderId?: number;
  attribution?: Record<string, unknown>;
  context?: Record<string, unknown>;
  contextScore?: number;
}

export interface AutopilotStats {
  tradesAttempted: number;
  tradesExecuted: number;
  tradesRejectedByRisk: number;
  errors: number;
}

export interface AutopilotSession {
  id: string;
  startedAt: string;
  status: string;
  cycles: number;
  accountId: number;
  primaryInstrument: string;
  instrumentList: string[];
  decisions: AutopilotDecision[];
  stats: AutopilotStats;
  lastScan: string;
  nextScan?: string;
}

export interface AutopilotStatus {
  running: boolean;
  session: AutopilotSession | null;
  risk: TopStepRiskState | null;
  challenge: ChallengeProgress | null;
}

export interface PxBar {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

// ─── Kaze Futures Signal ────────────────────────────────

export interface KazeFuturesSignal {
  rootKey?: string;
  symbol: string;
  contractName: string;
  contractId: string;
  description: string;
  category: 'equity_index' | 'commodity' | 'fx' | 'rates';
  direction: 'LONG' | 'SHORT' | 'FLAT';
  contextDirection?: 'LONG' | 'SHORT' | 'FLAT';
  contextTradeable?: boolean;
  directionalScore: number;
  contextScore?: number;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  tomiComposite: number;
  tomiRefreshedAt?: string;
  action: string;
  reason: string;
  topNews: KazeFuturesNewsItem[];
  proxyScores: { ticker: string; k_score: number }[];
  avgProxyScore: number;
  anchorProxyScores?: { ticker: string; k_score: number }[];
  proxyQuality?: 'DIRECT' | 'INDIRECT' | 'SYNTHETIC';
  proxyQualityReason?: string;
  requiresChartConfirmation?: boolean;
  requiredProxyAnchors?: string[];
  sentiment: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  macroRegime: string;
  macroTrend: string;
  vixLevel: number;
  vixRegime: string;
  riskPosture: string;
  liquidityState: string;
  briefingExcerpt: string | null;
  longDrivers: string[];
  shortDrivers: string[];
  timestamp: string;
}

export interface KazeFuturesNewsItem {
  headline: string;
  source: string;
  impact: 'HIGH' | 'MEDIUM' | 'LOW';
  direction: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  sentiment_score: number;
  kaze_score: number;
  timestamp: string;
}

export interface KazeSignalsMeta {
  macroRegime: string;
  macroTrend: string;
  vixLevel: number;
  vixRegime: string;
  riskPosture: string;
  globalSentiment: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  liquidityState: string;
  generatedAt: string;
}

export type AnalysisSide = 'long' | 'short' | 'neutral';
export type ChartGrade = 'A_PLUS' | 'A' | 'B' | 'C' | 'F';

export interface AnalysisVisualizationBar {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
  ema8?: number;
  ema21?: number;
  ema34?: number;
  ema50?: number;
}

export interface AnalysisIndicatorSnapshot {
  ema8: number;
  ema21: number;
  ema34: number;
  ema50: number;
  cloudTop: number;
  cloudBottom: number;
  emaTrend: 'bull_stack' | 'bear_stack' | 'mixed';
  emaSlope: 'up' | 'down' | 'flat';
  cloudTrend: 'bull_cloud' | 'bear_cloud' | 'flat_cloud';
  cloudSignal: 'buy_pullback' | 'buy_breakout' | 'short_pullback' | 'short_breakdown' | 'none';
  cloudSignalDirection: AnalysisSide | null;
  atr14: number;
  lastClose: number;
}

export interface ChartZoneRecommendation {
  zone: [number, number];
  condition: string;
  invalidation: number;
  target: number;
  qualityIfTriggered: ChartGrade;
}

export interface ChartIntelligenceVisualization {
  symbol: string;
  timeframe: string;
  sideBias: AnalysisSide;
  auctionPhase: string;
  setupType: string;
  structureGrade: ChartGrade;
  locationGrade: ChartGrade;
  timingGrade: ChartGrade;
  riskRewardGrade: ChartGrade;
  finalChartGrade: ChartGrade;
  recommendedAction: string;
  keyLevels: Record<string, unknown>;
  metrics: Record<string, number | string | boolean | null | undefined>;
  reasons: string[];
  vetoReasons: string[];
  upgradeConditions: string[];
  cancelConditions: string[];
  bestLongSetup?: ChartZoneRecommendation;
  bestShortSetup?: ChartZoneRecommendation;
  apge?: {
    pathBias: string;
    pathType: string;
    auctionPhase: string;
    location: string;
    structureGrade: ChartGrade;
    locationGrade: ChartGrade;
    timingGrade: ChartGrade;
    riskRewardGrade: ChartGrade;
    geometryGrade: ChartGrade;
    overallGrade: ChartGrade;
    recommendedAction: string;
    keyLevels: Record<string, unknown>;
    metrics: Record<string, number | string | boolean | null | undefined>;
    reasons: string[];
    vetoReasons: string[];
    upgradeConditions: string[];
    cancelConditions: string[];
    bestLong?: Record<string, unknown>;
    bestShort?: Record<string, unknown>;
  };
  operatorSummary: string;
}

export interface TopStepAnalysisVisualization {
  symbol: string;
  candidateSide: AnalysisSide;
  generatedAt: string;
  timeframe: '1h';
  dataStatus: 'ready' | 'insufficient_bars' | 'contract_not_found';
  dataReason?: string;
  contract?: Pick<TopStepContract, 'id' | 'name' | 'description' | 'tickSize' | 'tickValue' | 'symbolId'>;
  bars: AnalysisVisualizationBar[];
  indicators?: AnalysisIndicatorSnapshot;
  chartIntel?: ChartIntelligenceVisualization;
}

// ─── API Calls ──────────────────────────────────────────

const topstepService = {
  // Auth
  async connect(): Promise<TopStepSession> {
    const res = await api.post('/topstep/auth/connect', {});
    return res.data.data;
  },

  async getAuthStatus(): Promise<TopStepSession> {
    const res = await api.get('/topstep/auth/status');
    return res.data.data;
  },

  // Account
  async resolveAccount(preferName?: string): Promise<TopStepAccount> {
    const res = await api.post('/topstep/account/resolve', preferName ? { preferName } : {});
    return res.data.data;
  },

  async getAccount(): Promise<TopStepAccount | null> {
    try {
      const res = await api.get('/topstep/account');
      return res.data.data;
    } catch { return null; }
  },

  // Contracts
  async getContracts(): Promise<{ contracts: TopStepContract[]; registry: { symbol: string; id: string; name: string; tickSize: number; tickValue: number }[] }> {
    const res = await api.get('/topstep/contracts');
    return res.data.data;
  },

  async getInstruments(): Promise<TopStepInstrumentRegistryResponse> {
    const res = await api.get('/topstep/instruments');
    return {
      defaults: res.data.defaults || [],
      instruments: res.data.instruments || [],
    };
  },

  // Positions
  async getPositions(): Promise<TopStepPositionsResponse> {
    try {
      const res = await api.get('/topstep/positions');
      return res.data.data;
    } catch { return { positions: [], totalUnrealized: 0, pnlIsRealtime: true, realtimeCount: 0 }; }
  },

  // Orders
  async getOrders(status?: number): Promise<TopStepOrder[]> {
    try {
      const qs = status !== undefined ? `?status=${status}` : '';
      const res = await api.get(`/topstep/orders${qs}`);
      return res.data.data || [];
    } catch { return []; }
  },

  async placeOrder(params: {
    contractSymbol: string;
    side: 'BUY' | 'SELL';
    contracts: number;
    type?: 'market' | 'limit';
    limitPrice?: number;
  }): Promise<TopStepOrder> {
    const res = await api.post('/topstep/order/place', params);
    return res.data.data;
  },

  async cancelOrder(orderId: number): Promise<boolean> {
    const res = await api.post('/topstep/order/cancel', { orderId });
    return res.data.success;
  },

  async closePosition(contractId: string, size?: number): Promise<{ success: boolean; message?: string }> {
    try {
      const res = await api.post('/topstep/positions/close', { contractId, size });
      return { success: true, message: res.data.data?.message };
    } catch (err: any) {
      return { success: false, message: err?.response?.data?.error || 'Close failed' };
    }
  },

  // Risk
  async getRisk(): Promise<TopStepRiskState> {
    const res = await api.get('/topstep/risk');
    return res.data.data;
  },

  async getChallengeProgress(): Promise<ChallengeProgress> {
    const res = await api.get('/topstep/risk/progress');
    return res.data.data;
  },

  async triggerKillSwitch(reason: string): Promise<TopStepRiskState> {
    const res = await api.post('/topstep/risk/kill-switch', { reason });
    return res.data.data;
  },

  async clearKillSwitch(): Promise<TopStepRiskState> {
    const res = await api.delete('/topstep/risk/kill-switch', { data: { confirm: 'CLEAR' } });
    return res.data.data;
  },

  async resetDaily(): Promise<TopStepRiskState> {
    const res = await api.post('/topstep/risk/reset-daily', {});
    return res.data.data;
  },

  // Autopilot
  async startAutopilot(instruments: string[] = []): Promise<AutopilotSession> {
    const res = await api.post('/topstep/autopilot/start', { instruments });
    return res.data.data;
  },

  async stopAutopilot(): Promise<AutopilotSession> {
    const res = await api.post('/topstep/autopilot/stop', {});
    return res.data.data;
  },

  async getAutopilotStatus(): Promise<AutopilotStatus> {
    const res = await api.get('/topstep/autopilot/status');
    return res.data.data;
  },

  // Bars
  async getBars(contractId: string, unit: number = 3, unitNumber: number = 1, limit: number = 200): Promise<PxBar[]> {
    try {
      const qs = new URLSearchParams({
        contractId,
        unit: String(unit),
        unitNumber: String(unitNumber),
        limit: String(limit),
      });
      const res = await api.get(`/topstep/bars?${qs}`);
      return res.data.data || [];
    } catch { return []; }
  },

  // Kaze futures signals — server-side per-contract intelligence
  async getKazeFuturesSignals(_contracts?: TopStepContract[]): Promise<KazeFuturesSignal[]> {
    const res = await api.get('/topstep/kaze/signals');
    if (res.data?.success === false) throw new Error(res.data.error || 'Kaze signal request failed');
    const signals = res.data.data?.signals;
    return Array.isArray(signals) ? signals : [];
  },

  async getKazeSignalsMeta(): Promise<KazeSignalsMeta | null> {
    const res = await api.get('/topstep/kaze/signals');
    if (res.data?.success === false) throw new Error(res.data.error || 'Kaze signal request failed');
    return res.data.data?.meta || null;
  },

  // ── Per-Account (multi-account) endpoints ──────────────

  async getAccounts(): Promise<TopStepAccountStatus[]> {
    try {
      const res = await api.get('/topstep/accounts/status');
      const accounts = res.data.data?.accounts;
      if (Array.isArray(accounts) && accounts.length > 0) return accounts;
    } catch { /* fall through to discovery endpoint */ }

    const res = await api.get('/topstep/accounts');
    const accounts = res.data.data;
    return Array.isArray(accounts) ? accounts : [];
  },

  async getInstanceAccounts(instanceId: string): Promise<TopStepAccountStatus[]> {
    const encodedInstanceId = encodeURIComponent(instanceId);
    const res = await api.get(`/topstep/instances/${encodedInstanceId}/status`);
    const accounts = res.data.data?.accounts;
    return Array.isArray(accounts) ? accounts : [];
  },

  async getAccountRisk(accountId: number): Promise<TopStepRiskState | null> {
    try {
      const res = await api.get(`/topstep/accounts/${accountId}/risk`);
      return res.data.data || null;
    } catch { return null; }
  },

  async getAccountProgress(accountId: number): Promise<ChallengeProgress | null> {
    try {
      const res = await api.get(`/topstep/accounts/${accountId}/risk`);
      const d = res.data.data;
      if (!d) return null;
      return { targetPct: d.targetPct, riskUsedPct: d.riskUsedPct, postureLabel: d.postureLabel, onTrack: d.onTrack, summary: d.summary };
    } catch { return null; }
  },

  async startAccountAutopilot(accountId: number, instruments: string[] = []): Promise<AutopilotSession | null> {
    try {
      const res = await api.post(`/topstep/accounts/${accountId}/start`, {
        instruments,
        confirmLiveTrading: 'I_UNDERSTAND_LIVE_TRADING',
      });
      return res.data.data || null;
    } catch { return null; }
  },

  async stopAccountAutopilot(accountId: number): Promise<AutopilotSession | null> {
    try {
      const res = await api.post(`/topstep/accounts/${accountId}/stop`);
      return res.data.data || null;
    } catch { return null; }
  },

  async startInstanceAccountAutopilot(instanceId: string, accountId: number, instruments: string[] = []): Promise<AutopilotSession | null> {
    try {
      const encodedInstanceId = encodeURIComponent(instanceId);
      const res = await api.post(`/topstep/instances/${encodedInstanceId}/accounts/${accountId}/start`, {
        instruments,
        confirmAutoStart: 'I_UNDERSTAND_REMOTE_AUTOPILOT_START',
      });
      return res.data.data || null;
    } catch { return null; }
  },

  async stopInstanceAccountAutopilot(instanceId: string, accountId: number): Promise<AutopilotSession | null> {
    try {
      const encodedInstanceId = encodeURIComponent(instanceId);
      const res = await api.post(`/topstep/instances/${encodedInstanceId}/accounts/${accountId}/stop`);
      return res.data.data || null;
    } catch { return null; }
  },

  async clearAccountKillSwitch(accountId: number): Promise<void> {
    await api.post(`/topstep/accounts/${accountId}/risk/kill-switch/clear`, { authorizedBy: 'admin' });
  },

  async getAccountPositions(accountId: number): Promise<TopStepPositionsResponse> {
    try {
      const res = await api.get(`/topstep/accounts/${accountId}/positions`);
      return res.data.data;
    } catch { return { positions: [], totalUnrealized: 0, pnlIsRealtime: true, realtimeCount: 0 }; }
  },

  async getAccountOrders(accountId: number, status?: number): Promise<TopStepOrder[]> {
    try {
      const qs = status !== undefined ? `?status=${status}` : '';
      const res = await api.get(`/topstep/accounts/${accountId}/orders${qs}`);
      return res.data.data || [];
    } catch { return []; }
  },

  async getAccountAutopilotStatus(accountId: number): Promise<AutopilotStatus | null> {
    try {
      const res = await api.get(`/topstep/accounts/${accountId}/autopilot/status`);
      return res.data.data || null;
    } catch { return null; }
  },

  async getAccountAnalysisVisualization(accountId: number, symbol: string, side: AnalysisSide = 'neutral'): Promise<TopStepAnalysisVisualization | null> {
    try {
      const encodedSymbol = encodeURIComponent(symbol.trim().toUpperCase());
      const res = await api.get(`/topstep/accounts/${accountId}/analysis/${encodedSymbol}?side=${side}`);
      return res.data.data || null;
    } catch { return null; }
  },

  async getAccountTrades(accountId: number, limit: number = 100): Promise<any[]> {
    try {
      const res = await api.get(`/topstep/accounts/${accountId}/trades?limit=${limit}`);
      return res.data.data || [];
    } catch { return []; }
  },

  async getAccountChallengeProgress(accountId: number): Promise<ChallengeProgress | null> {
    try {
      const res = await api.get(`/topstep/accounts/${accountId}/risk/progress`);
      return res.data.data || null;
    } catch { return null; }
  },

  async placeAccountOrder(accountId: number, params: {
    contractSymbol: string;
    side: 'BUY' | 'SELL';
    contracts: number;
    type?: 'market' | 'limit';
    limitPrice?: number;
  }): Promise<TopStepOrder> {
    const res = await api.post(`/topstep/accounts/${accountId}/order/place`, params);
    return res.data.data;
  },
};

export default topstepService;
